import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// ── Types ────────────────────────────────────────────────────

export type ProductFilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than';

export interface ProductFilterRule {
  field: string;
  operator: ProductFilterOperator;
  value: string;
}

const OPERATORS: { value: ProductFilterOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than', label: 'less than' },
];

const NO_VALUE_OPS = new Set<ProductFilterOperator>(['is_empty', 'is_not_empty']);

// ── Apply Filters ────────────────────────────────────────────

export function applyProductFilters<T extends { raw_data: Record<string, unknown> }>(
  products: T[],
  filters: ProductFilterRule[],
): T[] {
  if (filters.length === 0) return products;

  return products.filter((product) =>
    filters.every((filter) => {
      if (!filter.field) return true;
      const rawVal = product.raw_data[filter.field];
      const str = rawVal == null ? '' : String(rawVal).toLowerCase();
      const target = (filter.value ?? '').toLowerCase();

      switch (filter.operator) {
        case 'equals':
          return str === target;
        case 'not_equals':
          return str !== target;
        case 'contains':
          return str.includes(target);
        case 'not_contains':
          return !str.includes(target);
        case 'is_empty':
          return str === '';
        case 'is_not_empty':
          return str !== '';
        case 'greater_than':
          return parseFloat(str) > parseFloat(target);
        case 'less_than':
          return parseFloat(str) < parseFloat(target);
        default:
          return true;
      }
    }),
  );
}

// ── Component ────────────────────────────────────────────────

interface ProductFilterBarProps {
  filters: ProductFilterRule[];
  onChange: (filters: ProductFilterRule[]) => void;
  fields: string[];
  formatHeader: (key: string) => string;
}

export function ProductFilterBar({ filters, onChange, fields, formatHeader }: ProductFilterBarProps) {
  const addFilter = () => {
    onChange([...filters, { field: '', operator: 'contains', value: '' }]);
  };

  const updateFilter = (index: number, updated: ProductFilterRule) => {
    onChange(filters.map((f, i) => (i === index ? updated : f)));
  };

  const removeFilter = (index: number) => {
    onChange(filters.filter((_, i) => i !== index));
  };

  const clearAll = () => onChange([]);

  return (
    <div className="space-y-2">
      {filters.map((filter, index) => (
        <div key={index} className="flex items-center gap-2">
          {index > 0 && (
            <span className="text-xs font-medium text-muted-foreground w-8 text-center shrink-0">
              AND
            </span>
          )}
          {index === 0 && <div className="w-8 shrink-0" />}

          {/* Field */}
          <Select
            value={filter.field || '_none'}
            onValueChange={(v) => updateFilter(index, { ...filter, field: v === '_none' ? '' : v })}
          >
            <SelectTrigger className="h-9 min-w-[140px] flex-1 rounded-lg border text-sm">
              <SelectValue placeholder="Field..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" disabled>Field...</SelectItem>
              {fields.map((f) => (
                <SelectItem key={f} value={f}>{formatHeader(f)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Operator */}
          <Select
            value={filter.operator}
            onValueChange={(v) => updateFilter(index, { ...filter, operator: v as ProductFilterOperator })}
          >
            <SelectTrigger className="h-9 min-w-[150px] rounded-lg border text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPERATORS.map((op) => (
                <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Value */}
          {!NO_VALUE_OPS.has(filter.operator) && (
            <Input
              value={filter.value}
              onChange={(e) => updateFilter(index, { ...filter, value: e.target.value })}
              placeholder="Value..."
              className="h-9 flex-1 min-w-[100px] rounded-lg border text-sm"
            />
          )}

          {/* Remove */}
          <button
            onClick={() => removeFilter(index)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      <div className="flex items-center gap-2 pt-1">
        <Button variant="outline" size="sm" className="rounded-lg text-xs h-8" onClick={addFilter}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Filter
        </Button>
        {filters.length > 0 && (
          <Button variant="ghost" size="sm" className="rounded-lg text-xs h-8 text-muted-foreground" onClick={clearAll}>
            Clear All
          </Button>
        )}
      </div>
    </div>
  );
}
